import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useSEO } from '../hooks/useSEO';

const CookiePolicy = () => {
  useSEO({
    title: "Cookie Policy | AM CARDMATE",
    description: "Learn about how AM CARDMATE uses cookies to improve your experience and provide personalized services.",
    image: "/assets/cookies.jpg",
    keywords: "cookie policy, cookies, tracking, website analytics, privacy"
  });
 
  useEffect(() => {
    const schema = {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Cookie Policy",
      "description": "Learn how AM CARDMATE uses cookies",
      "url": "https://amcardmate.com/cookies",
      "isPartOf": {
        "@type": "WebSite",
        "name": "AM CARDMATE"
      },
      "lastReviewed": new Date().toISOString(),
      "mainContentOfPage": {
        "@type": "WebPageElement",
        "cssSelector": ".cookie-policy-content"
      }
    };
    
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);
  }, []);

  const [showCookieSettings, setShowCookieSettings] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [showCookiePopup, setShowCookiePopup] = useState(false);
  const [cookiePreferences, setCookiePreferences] = useState({
    necessary: true,
    analytics: true,
    functional: true,
    marketing: false
  });
  
  useEffect(() => {
    const savedPreferences = localStorage.getItem('cookiePreferences');
    const hasSeenPopup = localStorage.getItem('hasSeenCookiePopup');
    
    if (savedPreferences) {
      setCookiePreferences(JSON.parse(savedPreferences));
    }
    
    if (!hasSeenPopup) {
      setTimeout(() => {
        setShowCookiePopup(true);
      }, 1000);
    }
  }, []);
  
  const cookieTypes = [
    {
      type: 'Necessary Cookies',
      key: 'necessary',
      icon: 'lock',
      required: true,
      description: 'Essential for the app to function properly. Cannot be disabled.',
      examples: [
        'Authentication and security',
        'User preferences',
        'Session management',
        'Load balancing'
      ],
      gradient: 'from-green-500 to-emerald-600'
    },
    {
      type: 'Analytics Cookies',
      key: 'analytics',
      icon: 'chart-line',
      required: false,
      description: 'Help us understand how users interact with our app to improve it.',
      examples: [
        'Usage statistics',
        'Feature popularity',
        'Performance monitoring',
        'Error tracking'
      ],
      gradient: 'from-blue-500 to-indigo-600'
    },
    {
      type: 'Functional Cookies',
      key: 'functional',
      icon: 'cog',
      required: false,
      description: 'Enable enhanced functionality and personalization.',
      examples: [
        'Language preferences',
        'Recently viewed cards',
        'UI customizations',
        'Saved searches'
      ],
      gradient: 'from-purple-500 to-pink-600'
    },
    {
      type: 'Marketing Cookies',
      key: 'marketing',
      icon: 'bullhorn',
      required: false,
      description: 'Used to deliver relevant advertisements (currently not used).',
      examples: [
        'Ad targeting',
        'Campaign effectiveness',
        'Retargeting',
        'Conversion tracking'
      ],
      gradient: 'from-orange-500 to-red-600'
    }
  ];

  const handleCookieToggle = (key) => {
    if (key !== 'necessary') {
      setCookiePreferences(prev => ({
        ...prev,
        [key]: !prev[key]
      }));
    }
  };

  const saveCookiePreferences = () => {
    localStorage.setItem('cookiePreferences', JSON.stringify(cookiePreferences));
    localStorage.setItem('hasSeenCookiePopup', 'true');
    
    setShowCookieSettings(false);
    setShowCookiePopup(false);
    setShowSuccessMessage(true);
    
    setTimeout(() => {
      setShowSuccessMessage(false);
    }, 3000);
  };

  const acceptAllCookies = () => {
    const allAccepted = {
      necessary: true,
      analytics: true,
      functional: true,
      marketing: true
    };
    setCookiePreferences(allAccepted);
    localStorage.setItem('cookiePreferences', JSON.stringify(allAccepted));
    localStorage.setItem('hasSeenCookiePopup', 'true');
    setShowCookieSettings(false);
    setShowCookiePopup(false);
    setShowSuccessMessage(true);
    setTimeout(() => {
      setShowSuccessMessage(false);
    }, 3000);
  };

  const rejectOptionalCookies = () => {
    const onlyNecessary = {
      necessary: true,
      analytics: false,
      functional: false,
      marketing: false
    };
    setCookiePreferences(onlyNecessary);
    localStorage.setItem('cookiePreferences', JSON.stringify(onlyNecessary));
    localStorage.setItem('hasSeenCookiePopup', 'true');
    setShowCookieSettings(false);
    setShowCookiePopup(false);
    setShowSuccessMessage(true);
    setTimeout(() => {
      setShowSuccessMessage(false);
    }, 3000);
  };

  return (
    <>
      {showSuccessMessage && (
        <div className="fixed top-24 right-4 bg-gradient-to-r from-green-500 to-emerald-600 text-white px-6 py-4 rounded-xl shadow-2xl z-50 flex items-center gap-3">
          <FontAwesomeIcon icon="check-circle" className="text-xl" />
          <span className="text-body font-medium">Cookie preferences saved successfully!</span>
        </div>
      )}

      {showCookiePopup && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-2xl z-50 p-6 md:p-8">
          <div className="container mx-auto max-w-6xl">
            <div className="flex flex-col lg:flex-row items-center justify-between gap-6">
              <div className="flex items-start gap-4 flex-1">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-400 to-purple-600 rounded-xl flex items-center justify-center text-white flex-shrink-0">
                  <FontAwesomeIcon icon="cookie-bite" className="text-xl" />
                </div>
                <div>
                  <h3 className="text-h5 text-dark mb-2">We Value Your Privacy 🍪</h3>
                  <p className="text-body text-gray">
                    We use cookies to enhance your experience, analyze site usage, and assist in our marketing efforts. 
                    By continuing to use AM CARDMATE, you agree to our use of cookies.
                  </p>
                </div>
              </div>
              <div className="flex flex-wrap gap-3">
                <button
                  onClick={acceptAllCookies}
                  className="btn btn-primary"
                >
                  Accept All
                </button>
                <button
                  onClick={() => setShowCookieSettings(true)}
                  className="btn btn-outline"
                >
                  Manage Preferences
                </button>
                <button
                  onClick={rejectOptionalCookies}
                  className="btn bg-gray-200 text-dark hover:bg-gray-300"
                >
                  Essential Only
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <section className="pt-20 md:pt-24 lg:pt-32 pb-24 bg-gradient-to-b from-white via-blue-50/30 to-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-gradient-to-br from-blue-200/20 to-purple-200/20 rounded-full filter blur-3xl" />
        <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-gradient-to-tr from-purple-200/20 to-pink-200/20 rounded-full filter blur-3xl" />
        
        <div className="container mx-auto px-4 sm:px-6 relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            <div className="badge badge-purple inline-flex mb-6">
              <FontAwesomeIcon icon="cookie-bite" />
              <span>Cookie Policy</span>
            </div>
            
            <h1 className="text-h1 mb-6">
              Our Cookie <span className="text-gradient-blue-pink">Policy</span>
            </h1>
            
            <p className="text-body-lg text-gray max-w-3xl mx-auto mb-10 leading-relaxed">
              Transparency matters. Learn how we use cookies to provide you with the best possible experience 
              while respecting your privacy choices.
            </p>
            
            <button
              onClick={() => setShowCookieSettings(true)}
              className="btn btn-primary group"
            >
              <FontAwesomeIcon icon="cog" />
              <span>Manage Cookie Preferences</span>
              <FontAwesomeIcon 
                icon="arrow-right" 
                className="ml-1 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300" 
              />
            </button>
          </div>
        </div>
      </section>

      {showCookieSettings && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setShowCookieSettings(false)}
        >
          <div
            className="bg-white rounded-3xl p-8 max-w-3xl w-full max-h-[80vh] overflow-y-auto shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="text-h3 text-dark">Cookie Preferences</h2>
                <p className="text-body text-gray mt-2">Customize your cookie settings</p>
              </div>
              <button
                onClick={() => setShowCookieSettings(false)}
                className="w-12 h-12 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors flex items-center justify-center"
              >
                <FontAwesomeIcon icon="times" className="text-gray-600 text-xl" />
              </button>
            </div>
            
            <div className="space-y-6 mb-8">
              {cookieTypes.map((cookie, index) => (
                <div 
                  key={index} 
                  className="bg-gradient-to-br from-gray-50 to-white rounded-2xl p-6 border border-gray-100"
                >
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-4">
                      <div className={`w-12 h-12 bg-gradient-to-br ${cookie.gradient} rounded-xl flex items-center justify-center text-white`}>
                        <FontAwesomeIcon icon={cookie.icon} />
                      </div>
                      <div>
                        <h3 className="text-body font-bold text-dark">{cookie.type}</h3>
                        {cookie.required && (
                          <span className="text-caption bg-gradient-to-r from-green-500 to-emerald-600 text-white px-3 py-1 rounded-full">
                            Always Active
                          </span>
                        )}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => !cookie.required && handleCookieToggle(cookie.key)}
                      disabled={cookie.required}
                      className={`relative inline-flex h-7 w-14 flex-shrink-0 cursor-pointer items-center rounded-full transition-colors duration-200 ease-in-out ${
                        cookie.required 
                          ? 'bg-gradient-to-r from-green-500 to-emerald-600 opacity-60 cursor-not-allowed' 
                          : cookiePreferences[cookie.key] 
                            ? 'bg-gradient-to-r from-blue-500 to-purple-600' 
                            : 'bg-gray-300'
                      }`}
                    >
                      <span
                        className={`inline-block h-6 w-6 transform rounded-full bg-white shadow-md transition-transform duration-200 ease-in-out ${
                          cookiePreferences[cookie.key] ? 'translate-x-8' : 'translate-x-0'
                        }`}
                      />
                    </button>
                  </div>
                  <p className="text-body text-gray mb-3">{cookie.description}</p>
                  <div className="flex flex-wrap gap-2">
                    {cookie.examples.map((example, idx) => (
                      <span key={idx} className="text-caption bg-gray-100 text-gray-600 px-3 py-1 rounded-full">
                        {example}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            
            <div className="flex gap-4">
              <button
                onClick={saveCookiePreferences}
                className="flex-1 btn btn-primary"
              >
                <FontAwesomeIcon icon="check" />
                <span>Save Preferences</span>
              </button>
              <button
                onClick={rejectOptionalCookies}
                className="flex-1 btn btn-outline"
              >
                <FontAwesomeIcon icon="times" />
                <span>Essential Only</span>
              </button>
            </div>
          </div>
        </div>
      )}

      <section className="py-16 md:py-24 bg-gradient-to-b from-white to-gray-50">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="max-w-5xl mx-auto">
            <div className="mb-16">
              <div className="text-center mb-12">
                <div className="badge badge-green inline-flex mb-6">
                  <FontAwesomeIcon icon="info-circle" />
                  <span>Understanding Cookies</span>
                </div>
                <h2 className="text-h2 text-dark mb-4">
                  What Are <span className="text-gradient-blue-pink">Cookies?</span>
                </h2>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all">
                  <div className="w-14 h-14 bg-gradient-to-br from-blue-400 to-purple-600 rounded-xl flex items-center justify-center text-white mb-4">
                    <FontAwesomeIcon icon="cookie-bite" className="text-2xl" />
                  </div>
                  <h3 className="text-h5 text-dark mb-3">What They Do</h3>
                  <p className="text-body text-gray">
                    Cookies are small text files stored on your device that help us remember your preferences, 
                    understand usage patterns, and improve your experience with AM CARDMATE.
                  </p>
                </div>
                
                <div className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all">
                  <div className="w-14 h-14 bg-gradient-to-br from-green-400 to-emerald-600 rounded-xl flex items-center justify-center text-white mb-4">
                    <FontAwesomeIcon icon="shield-alt" className="text-2xl" />
                  </div>
                  <h3 className="text-h5 text-dark mb-3">Your Privacy</h3>
                  <p className="text-body text-gray">
                    We respect your privacy. Our cookies don't contain personal information and you have 
                    full control over which types of cookies you accept.
                  </p>
                </div>
              </div>
            </div>

            <div className="mb-16">
              <div className="text-center mb-12">
                <div className="badge badge-blue inline-flex mb-6">
                  <FontAwesomeIcon icon="layer-group" />
                  <span>Cookie Categories</span>
                </div>
                <h2 className="text-h2 text-dark">
                  Types of Cookies <span className="text-gradient-blue-pink">We Use</span>
                </h2>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {cookieTypes.map((cookie, index) => (
                  <div 
                    key={index}
                    className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all"
                  >
                    <div className="flex items-start gap-4 mb-6">
                      <div className={`w-16 h-16 bg-gradient-to-br ${cookie.gradient} rounded-2xl flex items-center justify-center text-white shadow-lg`}>
                        <FontAwesomeIcon icon={cookie.icon} className="text-2xl" />
                      </div>
                      <div>
                        <h3 className="text-h4 text-dark">{cookie.type}</h3>
                        {cookie.required && (
                          <span className="text-body-sm text-green-600 font-medium">Always Active</span>
                        )}
                      </div>
                    </div>
                    
                    <p className="text-body text-gray mb-6">{cookie.description}</p>
                    
                    <div className="space-y-2">
                      <p className="text-body-sm font-semibold text-dark mb-3">Examples:</p>
                      {cookie.examples.map((example, idx) => (
                        <div key={idx} className="flex items-center gap-2">
                          <FontAwesomeIcon icon="check-circle" className="text-green-500 text-sm" />
                          <span className="text-body-sm text-gray">{example}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="mb-16">
              <div className="text-center mb-12">
                <div className="badge badge-pink inline-flex mb-6">
                  <FontAwesomeIcon icon="cog" />
                  <span>Cookie Control</span>
                </div>
                <h2 className="text-h2 text-dark">
                  Control Your <span className="text-gradient-blue-pink">Cookie Settings</span>
                </h2>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
                <div className="text-center">
                  <div className="w-20 h-20 bg-gradient-to-br from-blue-400 to-purple-600 rounded-2xl flex items-center justify-center text-white mx-auto mb-4 shadow-lg">
                    <FontAwesomeIcon icon="mobile-alt" className="text-3xl" />
                  </div>
                  <h3 className="text-body font-bold text-dark mb-2">In Our App</h3>
                  <p className="text-body-sm text-gray">Access cookie settings directly from your app preferences</p>
                </div>
                
                <div className="text-center">
                  <div className="w-20 h-20 bg-gradient-to-br from-green-400 to-emerald-600 rounded-2xl flex items-center justify-center text-white mx-auto mb-4 shadow-lg">
                    <FontAwesomeIcon icon="globe" className="text-3xl" />
                  </div>
                  <h3 className="text-body font-bold text-dark mb-2">Browser Settings</h3>
                  <p className="text-body-sm text-gray">Manage cookies through your browser's privacy settings</p>
                </div>
                
                <div className="text-center">
                  <div className="w-20 h-20 bg-gradient-to-br from-purple-400 to-pink-600 rounded-2xl flex items-center justify-center text-white mx-auto mb-4 shadow-lg">
                    <FontAwesomeIcon icon="sliders-h" className="text-3xl" />
                  </div>
                  <h3 className="text-body font-bold text-dark mb-2">Device Settings</h3>
                  <p className="text-body-sm text-gray">iOS and Android privacy settings for app tracking</p>
                </div>
              </div>
            </div>

            <section className="py-24 bg-gradient-blue-pink text-white text-center relative overflow-hidden rounded-3xl">
              <div className="absolute inset-0 bg-dots-pattern opacity-15"></div>
              
              <div className="absolute top-10 left-10 w-20 h-20 rounded-full bg-white opacity-20"></div>
              <div className="absolute bottom-10 right-10 w-28 h-28 rounded-full bg-white opacity-10"></div>
              <div className="absolute top-1/3 right-1/4 w-14 h-14 rounded-full bg-white opacity-15"></div>
              
              <div className="relative z-10">
                <h2 className="text-h3 mb-6">Questions About Our Cookie Policy?</h2>
                <p className="text-body-lg text-white/90 mb-8 max-w-2xl mx-auto">
                  We're here to help clarify any questions about how we use cookies and protect your privacy.
                </p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-2xl mx-auto">
                  <a 
                    href="mailto:support@amcardmate.com"
                    className="bg-white/20 backdrop-blur-sm rounded-xl p-6 hover:bg-white/30 transition-all flex items-center gap-3"
                  >
                    <FontAwesomeIcon icon="envelope" className="text-2xl" />
                    <div className="text-left">
                      <p className="text-body font-semibold">Email Us</p>
                      <p className="text-body-sm text-white/80">support@amcardmate.com</p>
                    </div>
                  </a>
                  
                  <button 
                    onClick={() => setShowCookieSettings(true)}
                    className="bg-white/20 backdrop-blur-sm rounded-xl p-6 hover:bg-white/30 transition-all flex items-center gap-3"
                  >
                    <FontAwesomeIcon icon="cog" className="text-2xl" />
                    <div className="text-left">
                      <p className="text-body font-semibold">Manage Cookies</p>
                      <p className="text-body-sm text-white/80">Update your preferences</p>
                    </div>
                  </button>
                </div>
              </div>
            </section>
          </div>
        </div>
      </section>
    </>
  );
};

export default CookiePolicy;